'use client';
import * as React from 'react';
import { SafeReact } from '@base-ui/utils/safeReact';
import { warn } from '@base-ui/utils/warn';
import { useIsoLayoutEffect } from '@base-ui/utils/useIsoLayoutEffect';
import { useNumberFieldRootContext } from '../root/NumberFieldRootContext';
import type { BaseUIComponentProps } from '../../internals/types';
import { useFieldRootContext } from '../../internals/field-root-context/FieldRootContext';
import { useRegisterFieldControl } from '../../internals/field-register-control/useRegisterFieldControl';
import { useFormContext } from '../../internals/form-context/FormContext';
import { useLabelableContext } from '../../internals/labelable-provider/LabelableContext';
import {
  getNumberLocaleDetails,
  isNumeralChar,
  parseNumber,
  ANY_MINUS_RE,
  ANY_PLUS_RE,
  ANY_MINUS_DETECT_RE,
  ANY_PLUS_DETECT_RE,
  FORMAT_CONTROL_DETECT_RE,
} from '../utils/parse';
import type { NumberFieldRootState } from '../root/NumberFieldRoot';
import { stateAttributesMapping } from '../utils/stateAttributesMapping';
import { useRenderElement } from '../../internals/useRenderElement';
import {
  createChangeEventDetails,
  createGenericEventDetails,
} from '../../internals/createBaseUIEventDetails';
import { formatNumber } from '../../utils/formatNumber';
import { useValueChanged } from '../../internals/useValueChanged';
import { REASONS } from '../../internals/reasons';
import { hasNumberFormatRoundingOptions, removeFloatingPointErrors } from '../utils/validate';

const NAVIGATE_KEYS = new Set([
  'Backspace',
  'Delete',
  'ArrowLeft',
  'ArrowRight',
  'Tab',
  'Enter',
  'Escape',
]);

/**
 * Whether every character in `text` is one the current format can render, so the field may hold it
 * even when it doesn't parse into a number yet (`-`, `1.`). Shared by typing and pasting so both
 * routes accept the same intermediate strings.
 */
function isAllowedText(text: string, allowedNonNumericKeys: Set<string>) {
  return Array.from(text).every(
    (char) =>
      isNumeralChar(char) ||
      ANY_MINUS_DETECT_RE.test(char) ||
      allowedNonNumericKeys.has(char) ||
      // Bidi/format controls are stripped by `parseNumber`; don't let them reject the string
      // (RTL locales insert them around exponent/currency signs, e.g. scientific notation).
      FORMAT_CONTROL_DETECT_RE.test(char),
  );
}

/**
 * The native input control in the number field.
 * Renders an `<input>` element.
 *
 * Documentation: [Base UI Number Field](https://base-ui.com/react/components/number-field)
 */
export const NumberFieldInput = React.forwardRef(function NumberFieldInput(
  componentProps: NumberFieldInput.Props,
  forwardedRef: React.ForwardedRef<HTMLInputElement>,
) {
  const { render, className, style, ...elementProps } = componentProps;

  const {
    isTextUserAuthored,
    releaseTextOwnership,
    formatOptionsRef,
    getAllowedNonNumericKeys,
    getStepAmount,
    id,
    incrementValue,
    inputMode,
    max,
    min,
    name,
    nameProp,
    setValue,
    state,
    setInputValue,
    locale,
    inputRef,
    onValueCommitted,
    lastChangedValueRef,
    hasPendingCommitRef,
    valueRef,
  } = useNumberFieldRootContext();
  const { disabled, readOnly, required, value, inputValue } = state;

  const { clearErrors } = useFormContext();
  const { validationMode, setTouched, setFocused, invalid, shouldValidateOnChange, validation } =
    useFieldRootContext();
  const { labelId } = useLabelableContext();

  const hasTouchedInputRef = React.useRef(false);
  const blockRevalidationRef = React.useRef(false);
  const pendingCaretRef = React.useRef<number | null>(null);

  useRegisterFieldControl(inputRef, id, value, undefined, !disabled, nameProp);

  // After a paste splices text into the controlled value, the browser would otherwise drop the
  // caret at the end of the new value. Restore it just after the inserted text.
  useIsoLayoutEffect(() => {
    if (pendingCaretRef.current != null) {
      const caret = pendingCaretRef.current;
      pendingCaretRef.current = null;
      inputRef.current?.setSelectionRange(caret, caret);
    }
  });

  useValueChanged(value, () => {
    clearErrors(name);

    if (blockRevalidationRef.current && !shouldValidateOnChange()) {
      blockRevalidationRef.current = false;
      return;
    }

    validation.change(value);
  });

  const inputProps: React.ComponentProps<'input'> = {
    id,
    required,
    disabled,
    readOnly,
    inputMode,
    value: inputValue,
    type: 'text',
    autoComplete: 'off',
    autoCorrect: 'off',
    spellCheck: 'false',
    'aria-roledescription': 'Number field',
    'aria-invalid': !disabled && invalid ? true : undefined,
    'aria-labelledby': labelId,
    // If the server's locale does not match the client's locale, the formatting may not match,
    // causing a hydration mismatch.
    suppressHydrationWarning: true,
    onFocus(event) {
      // Read-only inputs are still focusable; only the value-changing handlers stay gated on it.
      if (event.defaultPrevented || disabled) {
        return;
      }

      setFocused(true);

      if (hasTouchedInputRef.current) {
        return;
      }

      hasTouchedInputRef.current = true;

      // Browsers set selection at the start of the input field by default. We want to set it at
      // the end for the first focus.
      const target = event.currentTarget;
      const length = target.value.length;
      target.setSelectionRange(length, length);
    },
    onBlur(event) {
      if (event.defaultPrevented || disabled) {
        return;
      }

      setTouched(true);
      setFocused(false);

      if (readOnly) {
        return;
      }

      // Read before any write below reconciles the text back to `value`.
      const hadManualInput = isTextUserAuthored();
      const hadPendingProgrammaticChange = hasPendingCommitRef.current;
      const formatOptions = formatOptionsRef.current;

      if (inputValue.trim() === '') {
        // An already-empty value has nothing to clear. Calling `setValue` anyway would report the
        // clear a second time, because the text the user typed is still theirs at this point and
        // that alone makes a same-value input change worth reporting.
        if (value !== null) {
          const clearDetails = createChangeEventDetails(REASONS.inputClear, event.nativeEvent);
          setValue(null, clearDetails);
          // Respect a canceled clear, mirroring the non-empty blur path below. The empty text stays
          // with the user, but the field goes back to following `value` so a later external change
          // still repaints it.
          if (clearDetails.isCanceled) {
            releaseTextOwnership(formatNumber(value, locale, formatOptions));
            return;
          }
        }

        // Blur ends the user's ownership of the text even when there was nothing to store, so the
        // field can follow `value` again. Normalizes whitespace-only text away in passing.
        setInputValue(
          formatNumber(null, locale, formatOptions),
          createChangeEventDetails(REASONS.inputBlur, event.nativeEvent),
          'value',
        );

        if (validationMode === 'onBlur') {
          validation.commit(null);
        }
        // Don't report a commit when blurring an already-empty field that the user never
        // interacted with: nothing was cleared and no programmatic change is pending.
        if (hadManualInput || hadPendingProgrammaticChange || value !== null) {
          onValueCommitted(null, createGenericEventDetails(REASONS.inputClear, event.nativeEvent));
        }
        return;
      }

      const parsedValue = parseNumber(inputValue, locale, formatOptions);
      if (parsedValue === null) {
        // The text can never become a number (a lone `-`, `.`, or similar), so `value` stands and
        // the text goes back to matching it. Leaving it would strand the field showing something
        // its value contradicts, which is what the old formatting-sync fallback papered over —
        // and only when an unrelated re-render happened to follow.
        setInputValue(
          formatNumber(value, locale, formatOptions),
          createChangeEventDetails(REASONS.inputBlur, event.nativeEvent),
          'value',
        );

        // The field has settled on `value`, so it validates like any other blur.
        if (validationMode === 'onBlur') {
          validation.commit(value);
        }
        // The text itself produced no value, so it isn't worth a commit of its own. An earlier
        // change still waiting for one is: without this, it would leak into whichever unrelated
        // blur came next.
        if (hadPendingProgrammaticChange) {
          onValueCommitted(value, createGenericEventDetails(REASONS.inputBlur, event.nativeEvent));
        }
        return;
      }

      // Avoid applying Intl's default precision unless the format opts into rounding.
      const hasRoundingOptions = hasNumberFormatRoundingOptions(formatOptions);

      let committed: number | null;
      if (!hadManualInput && !hasRoundingOptions) {
        // No rounding options and no manual edit: the visible text is purely formatted
        // display, so keep the authoritative numeric value as-is rather than re-parsing the
        // rounded text and discarding precision (e.g. focus/blur with no edits, or blur after
        // a programmatic change).
        committed = value;
      } else if (hasRoundingOptions) {
        // Explicit rounding options apply to the committed value, whether typed or external.
        committed = removeFloatingPointErrors(parsedValue, formatOptions);
      } else {
        committed = parsedValue;
      }

      const nextEventDetails = createGenericEventDetails(REASONS.inputBlur, event.nativeEvent);
      const shouldUpdateValue = value !== committed;
      const shouldCommit = hadManualInput || shouldUpdateValue || hadPendingProgrammaticChange;

      // Use the stored value after `setValue` clamps it.
      let committedValue = committed;
      if (shouldUpdateValue) {
        const changeDetails = createChangeEventDetails(REASONS.inputBlur, event.nativeEvent);
        blockRevalidationRef.current = true;
        setValue(committed, changeDetails);
        if (changeDetails.isCanceled) {
          blockRevalidationRef.current = false;
          // A refused change leaves the text with the user so the edit can be corrected, which is
          // also what the canceled clear above does. The field still resumes following `value`, so
          // refusing one blur doesn't stop it from ever re-deriving its text again.
          releaseTextOwnership(formatNumber(value, locale, formatOptions));
          return;
        }
        committedValue = lastChangedValueRef.current;
        // If validation normalized back to the current value, `useValueChanged` won't fire to
        // reset the flag, so reset it here or the next external change won't revalidate.
        if (committedValue === value) {
          blockRevalidationRef.current = false;
        }
      }
      if (validationMode === 'onBlur') {
        validation.commit(committedValue);
      }
      if (shouldCommit) {
        onValueCommitted(committedValue, nextEventDetails);
      }

      // Reconcile the displayed text with what was actually stored.
      setInputValue(
        formatNumber(committedValue, locale, formatOptions),
        createChangeEventDetails(REASONS.inputBlur, event.nativeEvent),
        'value',
      );
    },
    onChange(event) {
      // Workaround for https://github.com/react/react/issues/9023
      if (event.nativeEvent.defaultPrevented) {
        return;
      }

      const targetValue = event.currentTarget.value;

      if (targetValue.trim() === '') {
        const clearTextDetails = createChangeEventDetails(REASONS.inputClear, event.nativeEvent);
        setInputValue(targetValue, clearTextDetails, 'user');
        // The value here is derived from the text that was just refused, so a vetoed text update
        // has to veto the whole keystroke. Applying it anyway would leave the text and the value
        // disagreeing, and blur resolves that disagreement by reading the text.
        if (clearTextDetails.isCanceled) {
          return;
        }
        setValue(null, createChangeEventDetails(REASONS.inputClear, event.nativeEvent), {
          projectText: false,
        });
        return;
      }

      // Update the input text immediately and only fire onValueChange if the typed value is
      // currently parseable into a number. This preserves good UX for IME
      // composition/partial input while still providing live numeric updates when possible.
      if (!isAllowedText(targetValue, getAllowedNonNumericKeys())) {
        return;
      }

      const parsedValue = parseNumber(targetValue, locale, formatOptionsRef.current);

      const changeTextDetails = createChangeEventDetails(REASONS.inputChange, event.nativeEvent);
      setInputValue(targetValue, changeTextDetails, 'user');

      if (changeTextDetails.isCanceled) {
        return;
      }

      if (parsedValue !== null) {
        setValue(parsedValue, createChangeEventDetails(REASONS.inputChange, event.nativeEvent), {
          projectText: false,
        });
      }
    },
    onKeyDown(event) {
      if (event.defaultPrevented || readOnly || disabled) {
        return;
      }

      const nativeEvent = event.nativeEvent;

      // Navigation and allowed keys (ArrowLeft, Tab, Enter, Escape, …) return early without
      // changing the value, so only the value-changing branches below reconcile the text.
      const hadManualInput = isTextUserAuthored();

      const allowedNonNumericKeys = getAllowedNonNumericKeys();

      let isAllowedNonNumericKey = allowedNonNumericKeys.has(event.key);

      const { decimal, currency, percentSign } = getNumberLocaleDetails(
        locale,
        formatOptionsRef.current,
      );

      const selectionStart = event.currentTarget.selectionStart;
      const selectionEnd = event.currentTarget.selectionEnd;
      const isAllSelected = selectionStart === 0 && selectionEnd === inputValue.length;

      const selectionContainsIndex = (index: number) =>
        selectionStart != null &&
        selectionEnd != null &&
        index >= selectionStart &&
        index < selectionEnd;

      // Only allow a single sign character: permit it when there is no existing sign of either
      // kind, when all text is selected, or when the selection covers the existing sign so it's
      // being replaced.
      const signGroups = [
        [ANY_MINUS_DETECT_RE, ANY_MINUS_RE],
        [ANY_PLUS_DETECT_RE, ANY_PLUS_RE],
      ] as const;
      signGroups.forEach(([detectRe, globalRe]) => {
        if (
          detectRe.test(event.key) &&
          Array.from(allowedNonNumericKeys).some((k) => detectRe.test(k))
        ) {
          const existingIndex = inputValue.search(globalRe);
          const isReplacingExisting = existingIndex !== -1 && selectionContainsIndex(existingIndex);
          isAllowedNonNumericKey =
            !(ANY_MINUS_DETECT_RE.test(inputValue) || ANY_PLUS_DETECT_RE.test(inputValue)) ||
            isAllSelected ||
            isReplacingExisting;
        }
      });

      // Only allow one of each symbol.
      [decimal, currency, percentSign].forEach((symbol) => {
        if (event.key === symbol) {
          const symbolIndex = inputValue.indexOf(symbol);
          const isSymbolHighlighted = selectionContainsIndex(symbolIndex);
          isAllowedNonNumericKey = symbolIndex === -1 || isAllSelected || isSymbolHighlighted;
        }
      });

      const isNavigateKey = NAVIGATE_KEYS.has(event.key);
      // Alt+ArrowUp/ArrowDown selects smallStep, so don't treat it as a bypass modifier.
      const isStepKey = event.key === 'ArrowUp' || event.key === 'ArrowDown';

      if (
        // Allow composition events (e.g., pinyin)
        // event.nativeEvent.isComposing does not work in Safari:
        // https://bugs.webkit.org/show_bug.cgi?id=165004
        event.which === 229 ||
        (event.altKey && !isStepKey) ||
        event.ctrlKey ||
        event.metaKey ||
        isAllowedNonNumericKey ||
        isNumeralChar(event.key) ||
        isNavigateKey
      ) {
        return;
      }

      // Home/End jump to the corresponding bound, but only when that bound is defined.
      let boundaryValue: number | null = null;
      if (event.key === 'Home' && min != null) {
        boundaryValue = min;
      } else if (event.key === 'End' && max != null) {
        boundaryValue = max;
      }

      // Let the browser handle multi-character keys we don't act on (PageUp, Insert, F-keys,
      // Home/End without min/max); invalid single characters are still blocked below.
      if (event.key.length > 1 && !isStepKey && boundaryValue === null) {
        return;
      }

      // Step from the authoritative numeric value unless the input has unsaved manual edits.
      // When the text is already synced, parsing the rounded display would collapse precision,
      // so pass no `currentValue` and let `incrementValue` fall back to the numeric state
      // (mirrors the button path).
      const currentValue = hadManualInput
        ? parseNumber(inputValue, locale, formatOptionsRef.current)
        : null;

      const amount = getStepAmount(event);

      // Prevent insertion of text or caret from moving.
      event.preventDefault();
      event.stopPropagation();

      const commitDetails = createGenericEventDetails(REASONS.keyboard, nativeEvent);

      let changed = false;
      if (isStepKey) {
        // When stepping from the synced numeric state, refresh the commit ref to the current
        // value so a canceled step can't commit a stale `lastChangedValueRef` left over from an
        // earlier change (mirrors the button path).
        if (!hadManualInput) {
          lastChangedValueRef.current = valueRef.current;
        }

        changed = incrementValue(amount, {
          direction: event.key === 'ArrowUp' ? 1 : -1,
          currentValue,
          event: nativeEvent,
          reason: REASONS.keyboard,
        });
      } else if (boundaryValue !== null) {
        changed = setValue(boundaryValue, createChangeEventDetails(REASONS.keyboard, nativeEvent));
      }

      // `changed` is only true when `setValue` applied the change, which records the stored
      // (clamped/snapped) value, so commit that rather than the pre-validation input.
      if (changed) {
        onValueCommitted(lastChangedValueRef.current, commitDetails);
      }
    },
    onPaste(event) {
      if (event.defaultPrevented || readOnly || disabled) {
        return;
      }

      let pastedData = '';

      try {
        pastedData = event.clipboardData?.getData('text/plain') ?? '';
      } catch {
        /* istanbul ignore else -- `process.env.NODE_ENV` is a build-time constant under test */
        if (process.env.NODE_ENV !== 'production') {
          const ownerStackMessage = SafeReact.captureOwnerStack?.() || '';
          warn(
            '<NumberField.Input> could not read clipboard text during paste handling.',
            ownerStackMessage,
          );
        }

        return;
      }

      // Prevent `onChange` from being called.
      event.preventDefault();

      // Insert the pasted text at the caret/selection instead of replacing the entire value,
      // matching native input behavior (e.g. pasting "5" into "123|" yields "1235").
      // The component renders `type="text"`, which always reports a selection range. Overriding
      // `type` with a selection-less one (`email`, `number`) is unsupported either way: the caret
      // restore above throws on those, so there is no working behavior to preserve here.
      const input = event.currentTarget;
      const selectionStart = input.selectionStart!;
      const selectionEnd = input.selectionEnd!;
      const nextText =
        inputValue.slice(0, selectionStart) + pastedData + inputValue.slice(selectionEnd);

      const parsedValue = parseNumber(nextText, locale, formatOptionsRef.current);

      // Accept the result when it parses, and otherwise when it's an intermediate string the
      // typing path would have accepted too (`-`, `1.`). Rejecting those here would make paste the
      // only route that can't reach a state `inputValue` is documented to hold.
      if (parsedValue === null && !isAllowedText(nextText, getAllowedNonNumericKeys())) {
        return;
      }

      // Report the text before the value, matching the typing path, so consumers mirroring both
      // callbacks observe the same order regardless of how the text arrived.
      const pasteTextDetails = createChangeEventDetails(REASONS.inputPaste, event.nativeEvent);
      setInputValue(nextText, pasteTextDetails, 'user');

      if (pasteTextDetails.isCanceled) {
        return;
      }

      pendingCaretRef.current = selectionStart + pastedData.length;

      if (parsedValue !== null) {
        setValue(parsedValue, createChangeEventDetails(REASONS.inputPaste, event.nativeEvent), {
          projectText: false,
        });
      }
    },
  };

  const element = useRenderElement('input', componentProps, {
    ref: [forwardedRef, inputRef],
    state,
    props: [inputProps, elementProps, (props) => validation.getValidationProps(disabled, props)],
    stateAttributesMapping,
  });

  return element;
});

export interface NumberFieldInputState extends NumberFieldRootState {}

export interface NumberFieldInputProps extends BaseUIComponentProps<
  'input',
  NumberFieldInputState,
  React.ComponentPropsWithRef<'input'>
> {
  /**
   * A user-friendly description of the input's role for assistive tech. This is a role
   * description, not an accessible name — use `Field.Label` or `aria-label` to name the control.
   * @default 'Number field'
   */
  'aria-roledescription'?: React.AriaAttributes['aria-roledescription'] | undefined;
}

export namespace NumberFieldInput {
  export type State = NumberFieldInputState;
  export type Props = NumberFieldInputProps;
}
